import React, { useEffect, useRef, useState } from 'react';
import { motion, useMotionValue, useSpring } from 'framer-motion';

const FloatingMascot = () => {
  const mascotX = useMotionValue(window.innerWidth * 0.15);
  const mascotY = useMotionValue(window.innerHeight * 0.3);
  const [rotation, setRotation] = useState(-15);
  const [isChasing, setIsChasing] = useState(false);
  const [isFleeing, setIsFleeing] = useState(false);

  const mouse = useRef({ x: -999, y: -999 });
  const pos = useRef({ x: window.innerWidth * 0.15, y: window.innerHeight * 0.3 });
  const vel = useRef({ x: 0.3, y: 0.1 });
  const animRef = useRef(null);
  const idlePhase = useRef(Math.random() * Math.PI * 2);

  const springX = useSpring(mascotX, { damping: 18, stiffness: 60, mass: 1.2 });
  const springY = useSpring(mascotY, { damping: 18, stiffness: 60, mass: 1.2 });

  const SIZE = 140;
  const FLEE_RADIUS = 160;
  const CHASE_RADIUS = 320;
  const FLEE_SPEED = 9;
  const CHASE_SPEED = 2.2;
  const IDLE_SPEED = 0.55;

  useEffect(() => {
    const handleMouseMove = (e) => {
      mouse.current = { x: e.clientX, y: e.clientY };
    };
    window.addEventListener('mousemove', handleMouseMove);

    let lastTime = performance.now();

    const animate = (now) => {
      const dt = Math.min((now - lastTime) / 16, 3);
      lastTime = now;

      const mx = mouse.current.x;
      const my = mouse.current.y;
      const cx = pos.current.x + SIZE / 2;
      const cy = pos.current.y + SIZE / 2;

      const dx = mx - cx;
      const dy = my - cy;
      const dist = Math.sqrt(dx * dx + dy * dy);

      const W = window.innerWidth;
      const H = window.innerHeight;

      let newRot = rotation;

      if (dist < FLEE_RADIUS) {
        // Flee — run away fast
        setIsChasing(false);
        setIsFleeing(true);
        const angle = Math.atan2(dy, dx);
        vel.current.x -= Math.cos(angle) * FLEE_SPEED * dt;
        vel.current.y -= Math.sin(angle) * FLEE_SPEED * dt;
        newRot = (Math.atan2(-vel.current.y, -vel.current.x) * 180) / Math.PI;
      } else if (dist < CHASE_RADIUS) {
        // Curious — slowly drift toward mouse
        setIsChasing(true);
        setIsFleeing(false);
        const angle = Math.atan2(dy, dx);
        vel.current.x += Math.cos(angle) * CHASE_SPEED * 0.04 * dt;
        vel.current.y += Math.sin(angle) * CHASE_SPEED * 0.04 * dt;
        newRot = (Math.atan2(dy, dx) * 180) / Math.PI - 90;
      } else {
        // Idle drift
        setIsChasing(false);
        setIsFleeing(false);
        idlePhase.current += 0.008 * dt;
        vel.current.x += Math.sin(idlePhase.current * 0.7) * 0.015 * dt;
        vel.current.y += Math.cos(idlePhase.current * 0.5) * 0.012 * dt;
        newRot = Math.sin(idlePhase.current * 0.4) * 18;
      }

      // Speed limits
      const speed = Math.sqrt(vel.current.x ** 2 + vel.current.y ** 2);
      const maxSpeed = dist < FLEE_RADIUS ? 14 : dist < CHASE_RADIUS ? 3.5 : IDLE_SPEED;
      if (speed > maxSpeed) {
        vel.current.x = (vel.current.x / speed) * maxSpeed;
        vel.current.y = (vel.current.y / speed) * maxSpeed;
      }

      // Damping
      const damp = dist < FLEE_RADIUS ? 0.88 : dist < CHASE_RADIUS ? 0.94 : 0.96;
      vel.current.x *= damp;
      vel.current.y *= damp;

      // Update position
      pos.current.x += vel.current.x * dt;
      pos.current.y += vel.current.y * dt;

      // Bounce off edges with padding
      const pad = 20;
      if (pos.current.x < -SIZE + pad) { pos.current.x = -SIZE + pad; vel.current.x *= -0.6; }
      if (pos.current.x > W - pad) { pos.current.x = W - pad; vel.current.x *= -0.6; }
      if (pos.current.y < -SIZE * 0.3 + pad) { pos.current.y = -SIZE * 0.3 + pad; vel.current.y *= -0.6; }
      if (pos.current.y > H - SIZE * 0.3 - pad) { pos.current.y = H - SIZE * 0.3 - pad; vel.current.y *= -0.6; }

      mascotX.set(pos.current.x);
      mascotY.set(pos.current.y);
      setRotation(newRot);

      animRef.current = requestAnimationFrame(animate);
    };

    animRef.current = requestAnimationFrame(animate);

    return () => {
      window.removeEventListener('mousemove', handleMouseMove);
      cancelAnimationFrame(animRef.current);
    };
  }, []);

  const glowColor = isFleeing
    ? 'rgba(153,104,124,0.4)'
    : isChasing
    ? 'rgba(195,122,83,0.3)'
    : 'rgba(74,90,103,0.15)';

  return (
    <motion.div
      className="fixed pointer-events-none"
      style={{
        x: springX,
        y: springY,
        zIndex: 1,
        width: SIZE,
        height: SIZE,
      }}
    >
      <motion.div
        animate={{ rotate: rotation }}
        transition={{ type: 'spring', stiffness: 80, damping: 14 }}
        style={{ width: '100%', height: '100%' }}
      >
        <motion.img
          src="https://media-manager-c.questera.ai/greta-media/f3431c01043f6ae8468982ada2c320c1b5f2e30df31f9c1fc9d61ccd4574fa71cea2ac7949ae73f0d2854f304172067e/images/aW1hZ2UvcG5n/43544a9c5099f8755ae077ccaa02bdba.png"
          alt="Space Mascot"
          animate={{
            scale: isFleeing ? 1.12 : isChasing ? 1.05 : 1,
            opacity: isFleeing ? 0.9 : isChasing ? 0.85 : 0.72,
            filter: isFleeing
              ? `drop-shadow(0 0 18px ${glowColor}) brightness(0.9) contrast(1.1)`
              : isChasing
              ? `drop-shadow(0 0 14px ${glowColor}) brightness(0.88) contrast(1.05)`
              : `drop-shadow(0 0 10px ${glowColor}) brightness(0.82) contrast(1.02)`,
          }}
          transition={{ duration: 0.3 }}
          style={{ width: '100%', height: '100%', objectFit: 'contain' }}
        />
      </motion.div>
    </motion.div>
  );
};

export default FloatingMascot;